/**
 * Base Column Definitions for Production Line
 * These columns are common across all production line tabs
 */

import React from 'react';
import type { OrderTableColumn } from '../../../types/components';
import { orderHasImagesColumnData } from '../../../utils/orderTableHelpers';

/**
 * Common columns shared across all production line tabs
 * Tab-specific columns (workflow checkboxes, dates, etc.) are added via tab overrides
 */
export const BASE_PRODUCTION_COLUMNS: OrderTableColumn[] = [
  // ========================================
  // JOB CONTEXT (Production Line Specific)
  // ========================================
  { 
    key: 'customer_name', 
    label: 'Customer', 
    type: 'text', 
    group: 'job_context' 
  },
  { 
    key: 'website_job_number', 
    label: 'W Order No.', 
    type: 'text', 
    group: 'job_context',
    width: '120px',           // Fixed width required for sticky columns
    sticky: true,             // Frozen during horizontal scroll
    stickyLeft: '0px'         // First sticky column at left edge
  },
  { 
    key: 'delivery_postcode', 
    label: 'PostCode', 
    type: 'text', 
    group: 'job_context', 
    width: '100px',           // Fixed width required for sticky columns
    sticky: true,             // Frozen during horizontal scroll
    stickyLeft: '120px'       // Offset by first column width
  },
  
  // ========================================
  // BASIC ORDER INFORMATION
  // ========================================
  { 
    key: 'product_type', 
    label: 'Products', 
    type: 'text', 
    group: 'basic', 
    render: (value: any) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '-' 
  },
  { 
    key: 'glass_width', 
    label: 'Width (mm)', 
    type: 'number', 
    group: 'basic', 
    align: 'right'
    // Note: Spacer tab will override render function via tab overrides
  },
  { 
    key: 'glass_height', 
    label: 'Height (mm)', 
    type: 'number', 
    group: 'basic', 
    align: 'right'
    // Note: Spacer tab will override render function via tab overrides
  },
  { 
    key: 'diameter_mm', 
    label: 'Diameter (mm)', 
    type: 'number', 
    group: 'basic', 
    align: 'right'
  },
  { 
    key: 'quantity', 
    label: 'Qty', 
    type: 'number', 
    group: 'basic', 
    width: '80px', 
    align: 'center',
    condition: {
      styles: {
        backgroundColor: (value: any) => {
          const qty = typeof value === 'number' ? value : (value ? parseInt(value, 10) : 0);
          if (qty === 1) return 'bg-gray-100';
          if (qty === 2) return 'bg-teal-100';
          if (qty === 3) return 'bg-purple-100';
          if (qty === 4) return 'bg-pink-100';
          if (qty === 5) return 'bg-indigo-100';
          if (qty > 5) return 'bg-cyan-100';
          return '';
        },
        textColor: () => 'font-bold'
      }
    }
  },
  { 
    key: 'reference', 
    label: 'Reference', 
    type: 'text', 
    group: 'optional_details',
    textDisplay: { expandable: true, collapsedLines: 3 },
  },
  { 
    key: 'notes', 
    label: 'Notes', 
    type: 'text', 
    group: 'optional_details',
    textDisplay: { expandable: true, collapsedLines: 3 },
  },
  // ========================================
  // GLASS SPECIFICATIONS
  // ========================================
  { 
    key: 'glass_side_one_display', 
    label: 'Glass Side One', 
    type: 'text', 
    group: 'glass_specs',
    condition: {
      styles: {
        backgroundColor: (_v: any, order: any) => order.glass_side_one_is_arrival ? 'bg-blue-100' : ''
      }
    }
  },
  { 
    key: 'glass_side_two_display', 
    label: 'Glass Side Two', 
    type: 'text', 
    group: 'glass_specs',
    condition: {
      styles: {
        backgroundColor: (_v: any, order: any) => order.glass_side_two_is_arrival ? 'bg-blue-100' : ''
      }
    }
  },
  { 
    key: 'middle_glass_panel_display', 
    label: 'Glass Side Three', 
    type: 'text', 
    group: 'glass_specs',
    width: '180px',
    condition: {
      styles: {
        backgroundColor: (_v: any, order: any) => order.middle_glass_panel_is_arrival ? 'bg-blue-100' : ''
      }
    }
  },
  { 
    key: 'polished_edges', 
    label: 'Polished', 
    type: 'boolean', 
    group: 'glass_specs', 
    render: (value: any) => value ? React.createElement('span', { className: 'font-bold bg-cyan-100 px-2 py-1 rounded' }, 'POL') : '-' 
  },
  { 
    key: 'dubbed_corners', 
    label: 'Dubbed', 
    type: 'boolean', 
    group: 'glass_specs', 
    render: (value: any) => value ? React.createElement('span', { className: 'font-bold bg-purple-100 px-2 py-1 rounded' }, 'DUB') : '-' 
  },
  
  // ========================================
  // DRILLED HOLES
  // ========================================
  { 
    key: 'hole_size_mm', 
    label: 'Hole Size (mm)', 
    type: 'number', 
    group: 'drilled_holes', 
    align: 'right' 
  },
  { 
    key: 'hole_count', 
    label: 'Number of holes', 
    type: 'number', 
    group: 'drilled_holes', 
    align: 'center' 
  },
  { 
    key: 'hole_locations', 
    label: 'Hole Locations', 
    type: 'text', 
    group: 'drilled_holes' 
  },
  
  // ========================================
  // CUTOUTS
  // ========================================
  { 
    key: 'cutout_shape', 
    label: 'Cut-out shape', 
    type: 'text', 
    group: 'cutouts' 
  },
  { 
    key: 'cutout_count', 
    label: 'Number of Cutouts', 
    type: 'number', 
    group: 'cutouts', 
    align: 'center' 
  },
  { 
    key: 'cutout_diameter_mm', 
    label: 'Cut-out Diameter (mm)', 
    type: 'number', 
    group: 'cutouts', 
    align: 'right' 
  },
  { 
    key: 'cutout_position', 
    label: 'Cut-out Position', 
    type: 'text', 
    group: 'cutouts' 
  },
  
  // ========================================
  // ROUNDED CORNERS
  // ========================================
  { 
    key: 'rounded_corner_radius_mm', 
    label: 'Corner radius (mm)', 
    type: 'number', 
    group: 'rounded_corners', 
    align: 'right' 
  },
  { 
    key: 'rounded_corner_count', 
    label: 'Total radius corners', 
    type: 'number', 
    group: 'rounded_corners', 
    align: 'center' 
  },
  { 
    key: 'rounded_corner_locations', 
    label: 'Corner Locations', 
    type: 'text', 
    group: 'rounded_corners' 
  },
  
  // ========================================
  // DOUBLE GLAZED - SPACER DETAILS
  // ========================================
  { 
    key: 'spacer_bar_type', 
    label: 'Spacer Type', 
    type: 'text', 
    group: 'double_glazed' 
  },
  { 
    key: 'spacer_bar_color', 
    label: 'Spacer Colour', 
    type: 'text', 
    group: 'double_glazed' 
  },
  { 
    key: 'spacer_bar_thickness_mm', 
    label: 'Spacer Thickness (mm)', 
    type: 'number', 
    group: 'double_glazed', 
    align: 'right',
    render: (value: any) =>
      value === null || value === undefined || value === '' ? '-' : `${value} mm`,
  },
  { 
    key: 'argon_filled', 
    label: 'Argon Filled', 
    type: 'boolean', 
    group: 'double_glazed', 
    render: (value: any) => value ? React.createElement('span', { className: 'font-bold bg-green-100 px-2 py-1 rounded' }, 'ARG') : '-' 
  },
  
  // ========================================
  // DECORATIVE LEAD DETAILS
  // ========================================
  { 
    key: 'decorative_leaded_pattern', 
    label: 'Leaded Pattern', 
    type: 'text', 
    group: 'double_glazed' 
  },
  { 
    key: 'decorative_lead_color', 
    label: 'Lead Colour', 
    type: 'text', 
    group: 'double_glazed' 
  },
  { 
    key: 'decorative_lead_thickness_mm', 
    label: 'Lead Thickness (mm)', 
    type: 'number', 
    group: 'double_glazed', 
    align: 'right' 
  },
  { 
    key: 'decorative_lead_soldering_required', 
    label: 'Soldering', 
    type: 'boolean', 
    group: 'double_glazed', 
    render: (value: any) => value ? React.createElement('span', { className: 'font-bold bg-amber-100 px-2 py-1 rounded' }, 'SOL') : '-' 
  },
  
  // ========================================
  // DUPLEX BAR DETAILS
  // ========================================
  { 
    key: 'duplex_bar_type', 
    label: 'Duplex Bar Type', 
    type: 'text', 
    group: 'double_glazed' 
  },
  { 
    key: 'duplex_bar_thickness', 
    label: 'Duplex Thickness', 
    type: 'text', 
    group: 'double_glazed' 
  },
  
  // ========================================
  // GEORGIAN BAR DETAILS
  // ========================================
  { 
    key: 'georgian_bar_type', 
    label: 'Georgian Bar Type', 
    type: 'text', 
    group: 'double_glazed' 
  },
  { 
    key: 'georgian_bar_thickness', 
    label: 'Georgian Thickness', 
    type: 'text', 
    group: 'double_glazed' 
  },
  
  // ========================================
  // TRIPLE GLAZED - SECOND SPACER DETAILS
  // ========================================
  { 
    key: 'spacer_bar2_type', 
    label: 'Spacer2 Type', 
    type: 'text', 
    group: 'triple_glazed' 
  },
  { 
    key: 'spacer_bar2_color', 
    label: 'Spacer2 Colour', 
    type: 'text', 
    group: 'triple_glazed' 
  },
  { 
    key: 'spacer_bar2_thickness_mm', 
    label: 'Spacer2 Thickness (mm)', 
    type: 'number', 
    group: 'triple_glazed', 
    align: 'right',
    render: (value: any) =>
      value === null || value === undefined || value === '' ? '-' : `${value} mm`,
  },
  
  // ========================================
  // MIRROR FEATURES
  // ========================================
  { 
    key: 'aluminium_backing_foil', 
    label: 'Aluminium Foil', 
    type: 'boolean', 
    group: 'mirror_features', 
    render: (value: any) => value ? React.createElement('span', { className: 'font-bold bg-slate-200 px-2 py-1 rounded' }, 'ALF') : '-' 
  },
  { 
    key: 'safety_backing', 
    label: 'Safety Backing', 
    type: 'boolean', 
    group: 'mirror_features', 
    render: (value: any) => value ? React.createElement('span', { className: 'font-bold bg-rose-100 px-2 py-1 rounded' }, 'SB') : '-' 
  },
  
  // ========================================
  // SYSTEM INFO
  // ========================================
  { 
    key: 'created_at', 
    label: 'Created', 
    type: 'date', 
    group: 'system' 
  },
  
  // ========================================
  // IMAGES (Handled by OrderImagesCells component)
  // ========================================
  { 
    key: 'images_available', 
    label: 'Image', 
    type: 'component', 
    group: 'images', 
    width: '80px', 
    align: 'center',
    condition: { hasDataCheck: orderHasImagesColumnData },
  },
  { 
    key: 'image_types', 
    label: 'Image Type', 
    type: 'component', 
    group: 'images', 
    width: '120px',
    condition: { hasDataCheck: orderHasImagesColumnData },
  },
  { 
    key: 'image_actions', 
    label: 'View Drawing', 
    type: 'component', 
    group: 'images', 
    width: '80px', 
    align: 'center',
    condition: { hasDataCheck: orderHasImagesColumnData },
  }
];
